/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/

import { Row, Col, Card } from "react-bootstrap";
import { CentreContents, OrgChart } from "@tol/tol-ui";
import { ReactComponent as TreeOfSexLogo } from "../assets/tos-logo.svg";
import { Timeline } from "rsuite";
import Sònia from "../assets/headshots/headshot-2.png";
import Paul from "../assets/headshots/headshot-3.png";
import Lukas from "../assets/headshots/headshot-4.png";
import Kamil from "../assets/headshots/headshot-5.png";
import Dan from "../assets/headshots/headshot-6.png";
import Cibele from "../assets/headshots/headshot-7.png";
import Aurora from "../assets/headshots/headshot-8.png";
import Christelle from "../assets/headshots/headshot-9.png";
import Melissa from "../assets/headshots/headshot-10.png";
import Catlin from "../assets/headshots/headshot-11.png";

function Home() {
  const nameArray = [
    "Cibele Sotero-Caio",
    "Dan Jeffries",
    "Kamil Jaron",
    "Paul Jay",
    "Sònia Garcia",
    "Lukas Kratochvil",
    "Aurora Ruiz-Herrera",
    "Christelle Fraïsse",
    "Melissa A. Wilson",
    "Caitlin E. McDonough-Goldstein",
  ];

  const photoArray = [
    Cibele,
    Dan,
    Kamil,
    Paul,
    Sònia,
    Lukas,
    Aurora,
    Christelle,
    Melissa,
    Catlin,
  ];

  const orgChartData = [
    {
      title: "Database Integration",
      contact: "Christelle",
      email: "christelle.fraisse@univ-lille.fr",
      description:
        "The main objective of this team is to develop a strategy for the integration of existing databases with reproductive data with the Tree of Sex " +
        "database as well as the ways the Tree of Sex data can be integrated into other resources (such as GoaT). " +
        "The team collaborates with the Ontology Team on making sure the Tree of Sex Ontology captures all that is needed to appropriately import other databases. " +
        "With the Ethics Team, we work on making sure the databases are integrated with respect to the original creators with appropriate credit.",
    },
    {
      title: "Ethics & Inclusivity",
      contact: "Caitlin",
      email: "mcdonoughgoldstein@gmail.com",
      description:
        "We are committed to creating and maintaining a diverse, inclusive, safe, and supportive environment within the ToS consortium. " +
        "Our team supports the development and improvement of strategies for integrating our ethics and inclusivity vision into all aspects of the consortium and monitors the impact of these efforts. " +
        `We also are leading the Definition(s) of “Sex” project, which is exploring the ambiguity, critiques, and implications of terminology used in reproductive biology.`,
    },
    {
      title: "Funding",
      contact: "Aurora",
      email: "aurora.ruizherrera@uab.cat",
      description:
        "The aim of our team is to monitor the current funding landscape and identify opportunities for the consortium to secure funding and to coordinate applications for individual grants.",
    },
    {
      title: "Literature",
      contact: "Dan",
      email: "daniel.jeffries@unibe.ch",
      description:
        "The literature team defines and implements a strategy for searching literature, and distributing it among the members of the consortium. We consider and explore data mining " +
        "and automated extraction techniques for the database jointly with the degree of manual curation that would be needed for Tree of Sex to maintain the status of a trusted resource.",
    },
    {
      title: "Ontology",
      contact: "Cibele",
      email: "cs43@sanger.ac.uk",
      description:
        "We are developing and maintaining the Tree of Sex Ontology capturing everything that will be stored in the database. " +
        "This ontology will be the backbone for database integrity and essential for integration of multiple sources in the same dictionary. " +
        "We collaborate with nearly all other teams and individuals across the consortium as this behemoth task requires many different expertise " +
        "(from biology knowledge of taxon, through ontology development to ethical choices about word we use).",
    },
    {
      title: "Comms & Outreach",
      contact: "Kamil",
      email: "kj11@sanger.ac.uk",
      description:
        "We have a dual role in developing a strategy for both internal communication " +
        "(Newsletter, sharing training opportunities) and outreach (communicating Tree of Sex to the public, maintaining BlueSky account). " +
        "We recently started organising the Tree of Sex seminar series, follow us on BlueSky for more @treeofsex.bsky.social.",
    },
  ];

  const committeeInfoArray = [
    "The Tree of Life Programme, Sanger Institute, UK",
    "Institute for Ecology and Evolution, University of Bern, Switzerland",
    "The Tree of Life Programme, Sanger Institute, UK",
    "Center for GeoGenetics, University of Copenhagen, Copenhagen, Denmark",
    "Botanical Institute of Barcelona, CSIC, Spain",
    "Faculty of Science, Charles University, Czech Republic",
    "Faculty of Bioscience, Universitat Autonoma de Barcelona, Spain",
    "Evolution Ecologie Paléontologie, CNRS, Université de Lille, France",
    "Arizona State University, USA",
    "University of Wisconsin-Madison, USA; University of Vienna, Austria",
  ];

  const emailArray = [
    "cs43@sanger.ac.uk",
    "daniel.jeffries@unibe.ch",
    "kj11@sanger.ac.uk",
    "paul.yann.jay@gmail.com",
    "soniagarcia@ibb.csic.es",
    "kratoch1@natur.cuni.cz",
    "aurora.ruizherrera@uab.cat",
    "christelle.fraisse@univ-lille.fr",
    "mwilsons@asu.edu",
    "mcdonoughgoldstein@gmail.com",
  ];

  const profileLinkArray = [
    "https://www.sanger.ac.uk/person/sotero-caio-cibele/",
    "https://www.ee.iee.unibe.ch/about_us/team/persons/dr_jeffries_daniel_lee/index_eng.html",
    "https://www.sanger.ac.uk/group/jaron-group/",
    "",
    "https://www.ibb.csic.es/en/research/evolutionary-biology-genome-organisation-plant-uses/staff/sonia-garcia/",
    "",
    "",
    "",
    "",
  ];

  return (
    <div className="tos">
      <CentreContents>
        <div>
          <Row>
            <div
              className="col-lg-3"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <TreeOfSexLogo className="tos-logo" />
            </div>
            <div className="col-lg-8">
              <h2 style={{ marginTop: "18px" }}>Our Goal</h2>
              <p className="me-4">
                The Tree of Sex project will compile information for
                reproductive systems across eukaryotic life - from sexual vs
                asexual reproduction, to the evolution of separate sexes, and
                the degeneration of sex/mating-type chromosomes. This
                information will be centralised in a publicly accessible and
                curated database that will ultimately allow researchers to test
                wide-reaching questions relating to the evolution of
                reproductive strategies, and how they influence the life
                history, genomes, adaptation and diversification of organisms
                throughout nature. The Tree of Sex v2.0 builds off the success
                of the first iteration. See archived site at the top of this
                page.
              </p>
            </div>
          </Row>
          <Row className="mt-3">
            <Col>
              <h2>Project Roadmap</h2>
              <Timeline className="mb-4">
                <Timeline.Item>
                  June 2023
                  <ul>
                    <li> Prototype of the database</li>
                  </ul>
                </Timeline.Item>
                <Timeline.Item>
                  19th - 23rd June 2023
                  <ul>
                    <li> First Tree of Sex consortium workshop</li>
                    <li>
                      {" "}
                      Summary of the workshop can be found{" "}
                      <a href="https://sangerinstitute.blog/2023/08/03/new-tree-of-sex-project-collects-all-the-wild-ways-nature-reproduces/">
                        here
                      </a>
                    </li>
                  </ul>
                </Timeline.Item>
                <Timeline.Item>
                  <div>
                    June 2023
                    <ul>
                      <li> Registration for the consortium is open!</li>
                      <li> Recruitment of Members</li>
                    </ul>
                  </div>
                </Timeline.Item>
                <Timeline.Item>
                  <div>
                    November 2023
                    <ul>
                      <li> Initiation of Operations Teams</li>
                    </ul>
                  </div>
                </Timeline.Item>
                <Timeline.Item>
                  <div>
                    2nd - 5th July 2024
                    <ul>
                      <li> Tree of Sex v2.0 Annual workshop (Prague).</li>
                    </ul>
                  </div>
                </Timeline.Item>
                <Timeline.Item>
                  <div>
                    2024
                    <ul>
                      <li> Initiate Taxon and Topic Teams</li>
                      <li>
                        {" "}
                        Establishing workflow and interface of the database
                      </li>
                    </ul>
                  </div>
                </Timeline.Item>
                <Timeline.Item>
                  <div>
                    2025
                    <ul>
                      <li> Public release of database</li>
                      <li> Opening database for public contributors</li>
                      <li> Analyses of the database data</li>
                    </ul>
                  </div>
                </Timeline.Item>
              </Timeline>
            </Col>
            <div className="col-lg-9">
              <h2>Join Us!</h2>
              <p>
                We are an open and highly-collaborative initiative, currently
                with over 150 members. But our task is a big one, so we are
                always open to new members. Whether you want to join/lead a
                collaborative team to research a particular lineage or question,
                or are just interested in reproductive systems biology and not
                sure yet how you will contribute, all are welcome to join.
                <br />
                <br />
                To register, click the "Register" button at the top of the page,
                or click <a href="/profile">here</a>.
              </p>
              <h2 style={{ marginTop: "30px" }}>The Consortium</h2>
              <p>
                The ToS consortium is managed at the top level by the 10 members
                of the general committee. Each committee member is involved in
                leading the activities of one or more "operational teams", these
                are the teams that take care of the major tasks required for the
                project. If you are interested in joining one of the operational
                teams, you can indicate so during registration. If you have any
                questions, simply reach out to the contact listed for each team
                below.
              </p>
              <div>
                <h2 style={{ textAlign: "center", marginTop: "20px" }}>
                  General Committee
                </h2>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {nameArray.map((person, index) => {
                    return (
                      <div
                        className="card-container"
                        key={index}
                        style={{
                          display: "inline-block",
                          marginTop: "10px",
                          marginRight: "15x",
                          lineHeight: "normal",
                        }}
                      >
                        <Card style={{ width: "6rem" }}>
                          <Card.Img
                            alt="Headshot of person in committee"
                            src={photoArray[index]}
                            variant="top"
                          />
                          <Card.Body>
                            <Card.Text style={{ fontSize: "12px" }}>
                              {profileLinkArray[index] !== "" ? (
                                <a href={profileLinkArray[index]}>{person}</a>
                              ) : (
                                person
                              )}
                            </Card.Text>
                            <Card.Text
                              style={{ fontSize: "12px", marginBottom: "30px" }}
                            >
                              {committeeInfoArray[index]}
                            </Card.Text>
                            <Card.Text
                              style={{
                                fontSize: "12px",
                                bottom: "0",
                                position: "absolute",
                                wordBreak: "break-all",
                                marginTop: "20px",
                              }}
                            >
                              <a href={`mailto:${emailArray[index]}`}>
                                {emailArray[index]}
                              </a>
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Row>
          <Row className="org-chart-row">
            <OrgChart
              title={"Operations Teams"}
              data={orgChartData}
              subtitle="Click on each team to view more information and contact."
              bordered
              modalKeys={["title", "contact", "email", "description"]}
            />
          </Row>
          <Row>
            <h2 style={{ marginBottom: "10px" }}>Current Projects</h2>
            <h6>Tree of Sex Database</h6>
            <p style={{ textAlign: "justify", padding: "5px 12px 5px 12px" }}>
              The core objective of this consortium is to create and maintain a
              database of reproductive knowledge across Eukaryotes. This
              includes building an infrastructure that would allow for community
              work, integration with other databases, creating a centralised
              lists of literature as well as well defined Ontology defining
              relationships among all captured information. We use a flexible
              data model that allows both variable depth of knowledge per
              species as well as multiple records of the same information. If
              you would like to contribute, join the consortium and subscribe to
              the teams relevant to your interests.
            </p>
            <h6>Tree of Sex Ontology</h6>
            <p style={{ textAlign: "justify", padding: "5px 12px 5px 12px" }}>
              Capturing the complexity of Eukaryotic reproduction within a
              single database is a big challenge. Our strategy to make such
              integrated resources possible is to develop a Tree of Sex Ontology
              (TOSO) - a graph of reproductive knowledge that captures all the
              nuances of all the terminology that will be used in the database
              together with their respective terminology. Ontology will help us
              to overcome multiple meanings to the same words as well as
              multiple words denoting the same concept. We are developing the
              ontology using community standards to maximise reusability by
              other projects and allow integration with other existing
              ontologies. See{" "}
              <a
                href="https://github.com/Tree-of-Sex/ToS-Ontology"
                target="_blank"
                rel="noreferrer"
              >
                the Github project
              </a>{" "}
              for more details.
            </p>
            <h6>Definition(s) of "Sex"</h6>
            <p style={{ textAlign: "justify", padding: "5px 12px 12px 12px" }}>
              Part of ethically compiling all the information on eukaryotic
              reproduction is being open to ongoing conversations critiquing
              definitions related to sex, recognizing how understandings of sex
              have been influenced by cultural contexts, and reflecting on how
              scientific use of sex may in turn have a societal influence that
              impacts people. In this project we are creating space to explore
              the ambiguities of “sex” terminology and definitions. Our goal is
              to develop a more nuanced understanding of the origins,
              rationales, and implications for sex related terminology /
              definitions and provide a perspective on how to navigate the
              entangled complexity and implications of sex in our research.
            </p>
          </Row>
        </div>
      </CentreContents>
    </div>
  );
}

export default Home;
